import React, { FC, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import * as _ from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AddButton, CheckboxInput } from '@td-client-frontend/common';
import { Add } from '@td-client-frontend/icons';
import { DatePicker, ResizableTextArea } from '../../../../components/ReactHookForm';
import { ModalType } from './GroupCodesModal';
import { GroupCodesWithReadOnly } from '../../../../@types/Groups';
import { useGroupContext } from '../../../../context/GroupContext';

export const EditCodesBody: FC<{ path: string }> = ({ path }) => {
  const { watch, setValue } = useFormContext();
  const { initialValues } = useGroupContext();
  const [editCodes, checkedCodes, multiEditedCodes]: [GroupCodesWithReadOnly[], string[], string[]] = watch([
    'editCodes',
    'checkedCodes',
    'multiEditedCodes'
  ]);

  const { fields, remove } = useFieldArray({ name: 'editCodes' });
  const { remove: removeCheckedCodes, append: appendCheckedCodes } = useFieldArray({ name: 'checkedCodes' });

  const controlledFields: GroupCodesWithReadOnly[] = useMemo(() => {
    const readOnlyCodes: string[] = (_.get(initialValues, path, []) || []).map(
      (code: GroupCodesWithReadOnly) => code.code
    );
    return fields.map((field, index) => {
      return {
        ...editCodes[index],
        ...field,
        readOnly: readOnlyCodes.includes(editCodes[index].code)
      };
    });
  }, [editCodes, fields, initialValues, path]);

  const isFieldList: number = editCodes.length;

  const handleCheck = (field: GroupCodesWithReadOnly) => {
    const idx = checkedCodes.findIndex((item: string) => item === field.code);
    if (idx === -1) {
      appendCheckedCodes(field.code);
    } else {
      removeCheckedCodes(idx);
    }
    multiEditedCodes.length && setValue('multiEditedCodes', []);
  };

  const handleAdd = useCallback(() => {
    setValue('multiEditAction', '');
    setValue('checkedCodes', []);
    setValue('contentType', ModalType.Add);
    multiEditedCodes.length && setValue('multiEditedCodes', []);
  }, [multiEditedCodes.length, setValue]);

  return (
    <>
      <div className="overflow-auto max-h-64">
        {controlledFields.map((field, index) => {
          const isChecked = checkedCodes.includes(field.code);
          return (
            <div
              key={`${field.code}-${index}`}
              className={clsx(
                `grid grid-cols-12 gap-x-4 border-b-1 border-gray px-2`,
                multiEditedCodes.includes(field.code) && 'bg-green-20'
              )}
            >
              {isFieldList > 1 && (
                <CheckboxInput
                  checked={isChecked}
                  onChange={() => handleCheck(field)}
                  className="col-span-1 mt-9 p-1"
                />
              )}
              <ResizableTextArea
                maxHeight={8}
                name={`editCodes.${index}.code`}
                wrapperClasses="col-span-4 pt-3"
                readOnly={field?.readOnly}
                styles={clsx(field?.readOnly && 'col-span-4 flex items-center')}
              />
              <DatePicker
                name={`editCodes.${index}.start_dt`}
                wrapperClasses="col-span-3 pt-3"
                placeholder="MM/DD/YYYY"
              />
              <DatePicker
                name={`editCodes.${index}.end_dt`}
                wrapperClasses="col-span-3 pt-3"
                placeholder="MM/DD/YYYY"
              />
              <div className="col-span-1">
                {isFieldList && !field.readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="flex transform rotate-45 w-6 h-6 mt-9 justify-self-center items-center focus:outline-none active:outline-none hover:transform hover:scale-110"
                  >
                    <Add />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex w-full items-center justify-between">
        <AddButton label="Add new" onClick={handleAdd} />
      </div>
    </>
  );
};
